import {
  getAccessToken,
  refreshAccessToken,
} from "../Component/User/AuthUtils";

const jsonHeaders = {
  "Content-Type": "application/json",
};

function getClientCountryHint() {
  if (typeof window === "undefined") return "";
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (timeZone === "Asia/Kolkata" || timeZone === "Asia/Calcutta") {
    return "IN";
  }

  const locale = window.navigator?.language || "";
  const localeCountry = locale.includes("-") ? locale.split("-").pop() : "";
  return localeCountry ? localeCountry.toUpperCase().slice(0, 2) : "";
}

function locationHeaders() {
  const country = getClientCountryHint();
  return country ? { "X-WebCredAI-Country": country } : {};
}

function getApiUrl(endpoint) {
  const apiUrl = process.env.REACT_APP_API_URL;
  return `${apiUrl}/${endpoint}`;
}

function authHeaders() {
  const token = getAccessToken();
  const headers = { ...jsonHeaders, ...locationHeaders() };
  return token
    ? { ...headers, Authorization: `Bearer ${token}` }
    : headers;
}

async function parseJsonResponse(response) {
  return response.json().catch(() => ({}));
}

function parseDownloadFilename(contentDisposition) {
  if (!contentDisposition) return "";
  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch (error) {
      return utf8Match[1].trim();
    }
  }
  const basicMatch = contentDisposition.match(/filename\s*=\s*"?(.*?)"?(?:;|$)/i);
  return basicMatch?.[1]?.trim() || "";
}

async function handleAuthenticatedFailure(response, retry, retryRequest) {
  // 403 means "logged in but not allowed" for admin routes. Never refresh-loop it.
  if (response.status !== 401 || !retry) {
    return {
      response: null,
      error: response,
    };
  }

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    return {
      response: null,
      error: response,
    };
  }
  return retryRequest(false);
}

export const fetchPostData = async (endpoint, reqObj, retry = true) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      method: "POST",
      headers: authHeaders(),
      body: reqObj.getRequest(),
    });
    if (!response.ok) {
      return handleAuthenticatedFailure(response, retry, (nextRetry) =>
        fetchPostData(endpoint, reqObj, nextRetry)
      );
    }
    const data = await parseJsonResponse(response);
    return {
      response: data.results,
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error: error,
    };
  }
};

export const fetchGetData = async (endpoint, retry = true) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      method: "GET",
      headers: authHeaders(),
    });
    if (!response.ok) {
      return handleAuthenticatedFailure(response, retry, (nextRetry) =>
        fetchGetData(endpoint, nextRetry)
      );
    }
    const data = await parseJsonResponse(response);
    return {
      response: data.results,
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error: error,
    };
  }
};

export const fetchDownloadData = async (endpoint, retry = true) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      method: "GET",
      headers: authHeaders(),
    });
    if (!response.ok) {
      return handleAuthenticatedFailure(response, retry, (nextRetry) =>
        fetchDownloadData(endpoint, nextRetry)
      );
    }
    const blob = await response.blob();
    return {
      response: {
        blob,
        filename: parseDownloadFilename(response.headers.get("content-disposition")),
      },
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error,
    };
  }
};

export const loginHandler = async (reqObj) => {
  try {
    const response = await fetch(getApiUrl("users/login"), {
      method: "POST",
      headers: { ...jsonHeaders, ...locationHeaders() },
      body: reqObj.getRequest(),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      return {
        response: null,
        error: data?.detail || data?.message || `Login failed (${response.status})`,
      };
    }
    if (!data.ok) {
      return {
        response: null,
        error: data?.message || "Login failed",
      };
    }

    return {
      response: data.tokens,
      error: null,
    };
  } catch (data) {
    return {
      response: null,
      error: data?.message || "Login failed",
    };
  }
};

export const signupHandler = async (reqObj) => {
  try {
    const response = await fetch(getApiUrl("users/sign_up"), {
      method: "POST",
      headers: { ...jsonHeaders, ...locationHeaders() },
      body: reqObj.getRequest(),
    });
    if (!response.ok) {
      return {
        response: null,
        error: response,
      };
    }
    const data = await parseJsonResponse(response);

    return {
      response: data.results ?? data,
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error: error,
    };
  }
};

export const fetchGetDataNoAuth = async (endpoint) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      method: "GET",
      headers: { ...jsonHeaders, ...locationHeaders() },
    });
    if (!response.ok) {
      return {
        response: null,
        error: response,
      };
    }
    const data = await parseJsonResponse(response);
    return {
      response: data.results,
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error: error,
    };
  }
};

export const fetchPostDataNoAuth = async (endpoint, reqObj) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      method: "POST",
      headers: { ...jsonHeaders, ...locationHeaders() },
      body: reqObj.getRequest(),
    });
    if (!response.ok) {
      return {
        response: null,
        error: response,
      };
    }
    const data = await parseJsonResponse(response);
    return {
      response: data.results,
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error: error,
    };
  }
};

export const fetchDeleteData = async (endpoint, retry = true) => {
  try {
    const response = await fetch(getApiUrl(endpoint), {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!response.ok) {
      return handleAuthenticatedFailure(response, retry, (nextRetry) =>
        fetchDeleteData(endpoint, nextRetry)
      );
    }
    const data = await parseJsonResponse(response);
    return {
      response: data.results,
      error: null,
    };
  } catch (error) {
    return {
      response: null,
      error: error,
    };
  }
};
